import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DeleteProject } from "../components/DeleteProject";
import type { Customer, Page, Plan, Product, Project } from "../lib/types";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  ProjectStatusBadge,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
} from "../components/ui";

const STATUSES = ["proposed", "in_progress", "active", "suspended", "ended"] as const;

const HEADINGS: Record<string, { title: string; blurb: string }> = {
  admin: { title: "Projects", blurb: "What each customer owns, and whether it is healthy." },
  customer: { title: "Your projects", blurb: "What we have built for you, and where each one stands." },
};

export function Projects() {
  const { user } = useAuth();
  const role = user?.role ?? "customer";
  const isAdmin = role === "admin";
  const isOwner = user?.isOwner === true;
  const heading = HEADINGS[role] ?? HEADINGS["customer"]!;

  const [status, setStatus] = useState("");
  const [productId, setProductId] = useState("");
  const [internalOnly, setInternalOnly] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Which row's delete dialog is open. Held here rather than one dialog per
  // row, so only the project actually being deleted is ever mounted.
  const [deleting, setDeleting] = useState<Project | null>(null);

  const params = new URLSearchParams();
  if (status.length > 0) params.set("status", status);
  if (productId.length > 0) params.set("productId", productId);
  // Sent only when it narrows: unchecked means "everything", not "customer work
  // only". Sending `internal=false` would hide our own projects from a screen
  // whose filter claims to be off.
  if (internalOnly) params.set("internal", "true");
  if (cursor !== null) params.set("cursor", cursor);

  const projects = useQuery({
    queryKey: ["projects", params.toString()],
    queryFn: () => api<Page<Project>>(`/api/v1/admin/projects?${params.toString()}`),
  });

  // The catalogue is staff-only, so a customer gets the list without a product
  // filter rather than a filter that answers 403.
  const products = useQuery({
    queryKey: ["products"],
    queryFn: () => api<Product[]>("/api/v1/admin/products"),
    enabled: isAdmin,
  });

  const filtered = status.length > 0 || productId.length > 0 || internalOnly;

  return (
    <div className="space-y-6">
      <PageHeader
        title={heading.title}
        lede={heading.blurb}
        action={
          isAdmin ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New project
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-48">
          <Field label="Status">
            <Select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setCursor(null);
              }}
            >
              <option value="">All</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value.replace("_", " ")}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {isAdmin && (
          <>
            <div className="w-full sm:w-56">
              <Field label="Product">
                <Select
                  value={productId}
                  onChange={(event) => {
                    setProductId(event.target.value);
                    setCursor(null);
                  }}
                >
                  <option value="">All products</option>
                  {(products.data ?? []).map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <label className="flex items-center gap-2 py-2 text-sm text-muted">
              <input
                type="checkbox"
                checked={internalOnly}
                onChange={(event) => {
                  setInternalOnly(event.target.checked);
                  setCursor(null);
                }}
                className="h-4 w-4 rounded border-hairline-strong bg-base accent-accent"
              />
              Internal work only
            </label>
          </>
        )}
      </div>

      <Card>
        {projects.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : projects.isError ? (
          <div className="p-4">
            <ErrorNote>Could not load projects. Reload to try again.</ErrorNote>
          </div>
        ) : projects.data.items.length === 0 ? (
          <EmptyState>
            {filtered
              ? "Nothing matches those filters."
              : isAdmin
                ? "No projects yet. Start one as soon as there is work to track — an order can come later, or never."
                : "Nothing here yet. Your projects appear as soon as we open one for you."}
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Reference</Th>
                  <Th>Project</Th>
                  {role !== "customer" && <Th>Customer</Th>}
                  <Th>Product</Th>
                  <Th>Plan</Th>
                  <Th>Status</Th>
                  <Th>Licence</Th>
                  <Th>Orders</Th>
                  {isOwner && <Th className="text-right">Delete</Th>}
                </tr>
              </thead>
              <tbody>
                {projects.data.items.map((project) => (
                  <tr key={project.id} className="border-b border-hairline last:border-0">
                    <Td label="Reference">
                      <Link
                        to={`/projects/${project.id}`}
                        className="tabular text-accent underline-offset-4 hover:underline"
                      >
                        {project.reference}
                      </Link>
                    </Td>
                    <Td label="Project">
                      <span className="block">
                        <span className="block">{project.name}</span>
                        {project.internal && (
                          <span className="mt-1 block">
                            <Badge tone="quiet">Internal</Badge>
                          </span>
                        )}
                      </span>
                    </Td>
                    {role !== "customer" && (
                      <Td label="Customer">
                        <span className="block">
                          <span className="block">{project.customer.name}</span>
                          <span className="block text-xs text-dim">{project.customer.email}</span>
                        </span>
                      </Td>
                    )}
                    <Td label="Product" className="text-muted">
                      {project.product.name}
                    </Td>
                    <Td label="Plan" className="text-muted">
                      {project.plan.name}
                    </Td>
                    <Td label="Status">
                      <ProjectStatusBadge status={project.status} />
                    </Td>
                    {/* A project with no licence is the normal state of hosting
                        and service work, so it reads as a fact rather than a
                        gap waiting to be filled. */}
                    <Td label="Licence" className="tabular text-muted">
                      {project.license === null ? (
                        <span className="font-sans text-dim">None</span>
                      ) : (
                        <Link
                          to={`/licenses/${project.license.id}`}
                          className="text-accent underline-offset-4 hover:underline"
                        >
                          ••••{project.license.keyLast4}
                        </Link>
                      )}
                    </Td>
                    <Td label="Orders" className="tabular text-muted">
                      {project._count.orders}
                    </Td>
                    {isOwner && (
                      <Td label="Delete" className="text-right">
                        <Button variant="ghost" onClick={() => setDeleting(project)}>
                          Delete
                        </Button>
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {(cursor !== null || projects.data?.nextCursor != null) && (
        <div className="flex justify-between">
          <Button onClick={() => setCursor(null)} disabled={cursor === null}>
            First page
          </Button>
          <Button
            onClick={() => setCursor(projects.data?.nextCursor ?? null)}
            disabled={projects.data?.nextCursor == null}
          >
            Next page
          </Button>
        </div>
      )}

      {creating && <NewProject onClose={() => setCreating(false)} />}

      {deleting !== null && (
        <DeleteProject
          project={deleting}
          open
          onCancel={() => setDeleting(null)}
          onDeleted={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

/**
 * Opens a project and nothing else. There is deliberately no price, no invoice
 * and no payment here: a project routinely exists before any money does — work
 * started on a handshake, a proposal being built out, something we run for
 * ourselves — and asking for a figure would imply one is owed.
 */
function NewProject({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [customerId, setCustomerId] = useState("");
  const [productId, setProductId] = useState("");
  const [planId, setPlanId] = useState("");
  const [name, setName] = useState("");
  const [deployment, setDeployment] = useState<"self_hosted" | "managed">("self_hosted");
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("proposed");
  const [internal, setInternal] = useState(false);
  const [hostnames, setHostnames] = useState("");
  const [notes, setNotes] = useState("");

  const customers = useQuery({
    queryKey: ["customers"],
    queryFn: () => api<Customer[]>("/api/v1/admin/customers"),
  });
  const products = useQuery({
    queryKey: ["products"],
    queryFn: () => api<Product[]>("/api/v1/admin/products"),
  });
  const plans = useQuery({
    queryKey: ["plans"],
    queryFn: () => api<Plan[]>("/api/v1/admin/plans"),
  });

  // A plan belongs to exactly one product and the server rejects a mismatch.
  // Filtering here makes that error unreachable rather than merely handled.
  const planOptions = (plans.data ?? []).filter((plan) => plan.productId === productId);

  const create = useMutation({
    mutationFn: () =>
      api<Project>("/api/v1/admin/projects", {
        method: "POST",
        body: {
          customerId,
          productId,
          planId,
          name: name.trim(),
          deployment,
          status,
          internal,
          hostnames: hostnames
            .split(/[\s,]+/)
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
          ...(notes.trim().length === 0 ? {} : { notes: notes.trim() }),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      onClose();
    },
  });

  return (
    <Card title="New project">
      <form
        className="space-y-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <p className="text-sm text-muted">
          A project is what the customer owns. It can exist long before any money does — this
          raises no order, sends no invoice and charges nothing.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Customer">
            <Select
              value={customerId}
              required
              onChange={(event) => setCustomerId(event.target.value)}
            >
              <option value="">Select a customer</option>
              {(customers.data ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} — {customer.email}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Name" hint="What the customer calls it — “Corporate website”.">
            <Input
              value={name}
              required
              maxLength={200}
              placeholder="Corporate website"
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field label="Product">
            <Select
              value={productId}
              required
              onChange={(event) => {
                setProductId(event.target.value);
                setPlanId("");
              }}
            >
              <option value="">Select a product</option>
              {(products.data ?? []).map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Plan" hint="What it runs on now. An upgrade moves it later.">
            <Select
              value={planId}
              required
              disabled={productId.length === 0}
              onChange={(event) => setPlanId(event.target.value)}
            >
              <option value="">
                {productId.length === 0 ? "Pick a product first" : "Select a plan"}
              </option>
              {planOptions.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Deployment" hint="Where it runs, not what it is.">
            <Select
              value={deployment}
              onChange={(event) => setDeployment(event.target.value as "self_hosted" | "managed")}
            >
              <option value="self_hosted">Customer hosts it</option>
              <option value="managed">We host it</option>
            </Select>
          </Field>

          <Field label="Status" hint="Proposed until somebody starts work.">
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value as (typeof STATUSES)[number])}
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value.replace("_", " ")}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm text-muted">
          <input
            type="checkbox"
            checked={internal}
            onChange={(event) => setInternal(event.target.checked)}
            className="h-4 w-4 rounded border-hairline-strong bg-base accent-accent"
          />
          Internal work — ours, with no customer money behind it
        </label>

        <Field label="Hostnames" hint="One per line. Where this runs.">
          <textarea
            value={hostnames}
            rows={3}
            placeholder="example.com&#10;www.example.com"
            className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 tabular text-ink placeholder:text-dim focus:border-accent"
            onChange={(event) => setHostnames(event.target.value)}
          />
        </Field>

        <Field label="Notes" hint="Anything about this project worth remembering later.">
          <textarea
            value={notes}
            rows={2}
            className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 text-sm text-ink focus:border-accent"
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>

        {create.isError && <ErrorNote>{(create.error as Error).message}</ErrorNote>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create project"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
